import React, {useRef, useState} from "react";
import * as ReactDOM from "react-dom";
import * as Sentry from "@sentry/browser";
import {useTranslation} from "react-i18next";
import {isCart, IUnsafeCartContext, useUnsafeCartContext} from "../CartContextProvider/CartContextProvider";
import {PayPalLoadingSpinner} from "../../lib/payments/paypal/BraintreeButton";
import ApplePayButton from "../../lib/payments/applepay/ApplePayButton";
import {expressPaymentsError} from "../../../helpers/errorHandling";
import {ApiError} from "../../../typescript-library";
import {PossibleNetworkError} from "../ShoppingCart/ShoppingCart";
import {updateUrlParams} from "../CartIcon/CartIconPortal";

type IAddToCartButtonProps = {
  itemId: number;
  showQuantity?: boolean;
  isSwiperAddToCartButton?: boolean;
  showExpressPayment?: boolean;
};

const buttonAttributes = (styleClasses: string | undefined) => ({
  className: `button product-cart__addbutton${!!styleClasses ? ` ${styleClasses}` : ""}`,
});

const AddToCartButtonPortal: React.FC<{targetNode: HTMLSpanElement | null; root: HTMLSpanElement | null}> = (props) => {
  const {targetNode, root} = props;

  React.useEffect(() => {
    if (!root || !targetNode) {
      return;
    }

    const problems = targetNode.getElementsByClassName("problemmm");
    if (problems.length) {
      root.appendChild(problems[0]);
    }
    targetNode.replaceWith(root);
  }, []);

  if (!root || !targetNode) {
    return null;
  }

  // if this is not there the item is most likely "nicht mehr lieferbar"
  const buttonPlaceholder = targetNode.querySelector<HTMLElement>(".product-cart-placeholder");

  if (!buttonPlaceholder || !buttonPlaceholder.dataset.itemid) {
    return null;
  }

  root.dataset.itemid = buttonPlaceholder.dataset.itemid;
  const itemId = parseInt(buttonPlaceholder.dataset.itemid, 10);

  return ReactDOM.createPortal(<AddToCartButton itemId={itemId} showExpressPayment={true} />, root);
};

export const AddToCartButton: React.FC<IAddToCartButtonProps> = ({
  itemId,
  showQuantity = true,
  isSwiperAddToCartButton,
  showExpressPayment = false,
}) => {
  const context = useUnsafeCartContext();
  const contextRef = useRef<IUnsafeCartContext>(null);
  contextRef.current = context;
  const {t} = useTranslation(["errorcodes", "translations"]);
  const [loading, setLoading] = useState<boolean>(false);

  let styleClasses = "button-large";
  if (isSwiperAddToCartButton) {
    styleClasses = "button-white";
  }
  if (!context) {
    return null;
  }

  let oldQuantity = 0;
  if (isCart(context)) {
    oldQuantity =
      context.cart.positions.find(
        (pos) => pos.type === "Item" && !!pos.reference?.itemId && pos.item?.itemId === itemId,
      )?.quantity ?? 0;
  }

  const addToCart = () => {
    const quantity = oldQuantity + 1;

    if (loading || context.loading) return;
    setLoading(true);

    if (isCart(context)) {
      void context.cart.addItem(itemId, quantity).then(() => {
        setLoading(false);
        context.setItemsAdded([{itemId, quantity, gtin: null}]);
      });
    } else {
      context.requestCart().then((result) => {
        const {cart, done} = result;
        cart.addItem(itemId, 1).then(() => {
          setLoading(false);
          context.setItemsAdded([{itemId, quantity: 1, gtin: null}]);
          done();
        }, Sentry.captureException);
      }, Sentry.captureException);
    }
  };

  const quantityIfInCart = oldQuantity > 0 && showQuantity ? ` (${oldQuantity})` : "";

  if (isSwiperAddToCartButton) {
    const ButtonContent = () => {
      if (loading) {
        return <PayPalLoadingSpinner />;
      }

      return (
        <React.Fragment>
          <span className="icon icon-link icon-cart icon-size--22" />
          <span className={"modal-swiper-basket-quantity"}>{quantityIfInCart}</span>
        </React.Fragment>
      );
    };

    // Show button with cart icon
    return (
      <button {...buttonAttributes(styleClasses)} onClick={addToCart}>
        <ButtonContent />
      </button>
    );
  }

  const onReturn = () => {
    if (isCart(contextRef.current)) {
      contextRef.current.setExternalErrors([]);
      contextRef.current.setIsCartVisible(true);
      contextRef.current.setIsIntoCheckout(true);
      updateUrlParams("checkout");
    }
  };

  const handleError = (
    error: ApiError | PossibleNetworkError,
    occurrence: string,
    additional?: Record<string, unknown>,
  ) => {
    // It's hard errors from the boundary, so no logging or visualization, just sentry tracking
    expressPaymentsError(error, occurrence, additional);
  };

  // Show button with text
  return (
    <React.Fragment>
      <button {...buttonAttributes(styleClasses)} onClick={addToCart}>
        {loading ? <PayPalLoadingSpinner background={"dark"} /> : t("general.addToShoppingCart") + quantityIfInCart}
      </button>
      {showExpressPayment && (
        <ApplePayButton
          isBlocked={loading || context.loading}
          place="pdp"
          handleError={handleError}
          onReturn={onReturn}
        />
      )}
    </React.Fragment>
  );
};

export default AddToCartButtonPortal;
